import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  ProductStockFilterKeys,
  ProductStockFilterOpts,
  TrackInventoryBoolean
} from "../../components/ProductStockPage/filters";
import {
  InitialProductStockFilterData_brands_edges_node,
  InitialProductStockFilterData_malls_edges_node
} from "../../types/InitialProductStockFilterData";
import {
  ProductStocksListUrlFilters,
  ProductStocksListUrlFiltersEnum,
  ProductStocksListUrlFiltersWithMultipleValues,
  ProductStocksListUrlQueryParams
} from "../../url";
export const ProductStocks_FILTERS_KEY = "ProductStocksFilters";

export function getFilterOpts(
  params: ProductStocksListUrlFilters,
  brands: {
    initial: InitialProductStockFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialProductStockFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): ProductStockFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    productStatus: {
      active: maybe(() => params.productStatus !== undefined, false),
      value: maybe(() => dedupeFilter(params.productStatus), [])
    },
    stockUpdateDate: {
      active: maybe(
        () =>
          [params.stockUpdateFrom, params.stockUpdateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.stockUpdateTo, ""),
        min: maybe(() => params.stockUpdateFrom, "")
      }
    },
    trackInventory: {
      active: !!params.trackInventory,
      value: maybe(() => params.trackInventory, "")
    },
    updateDate: {
      active: maybe(
        () =>
          [params.updateFrom, params.updateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.updateTo, ""),
        min: maybe(() => params.updateFrom, "")
      }
    },
    vertical: {
      active: !!params.vertical,
      value: maybe(() => dedupeFilter(params.vertical), [])
    }
  };
}

export function getFilterVariables(params: ProductStocksListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );

  const endDate = moment(`${endYear}-${endMonth}-${endDay} 00:00:00`)
    .add(1, "days")
    .format("YYYY-MM-DD HH:mm:ssZZ");

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    productStatus: params.productStatus || [],
    productUpdatedDate: getGteLteVariables({
      gte: params.updateFrom
        ? moment(`${params.updateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.updateTo
        ? moment(`${params.updateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    search: params.query,
    stocksUpdatedDate: getGteLteVariables({
      gte: params.stockUpdateFrom
        ? moment(`${params.stockUpdateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.stockUpdateFrom && params.updateFrom
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),
      lte: params.stockUpdateTo
        ? moment(`${params.stockUpdateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.stockUpdateTo && params.updateTo
        ? null
        : endDate
    }),
    trackInventory: params.trackInventory
      ? params.trackInventory === "true"
      : null,
    vertical: maybe(() => params.vertical, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<ProductStockFilterKeys>,
  params: ProductStocksListUrlFilters
): ProductStocksListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case ProductStockFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        ProductStocksListUrlFiltersWithMultipleValues.mall
      );
    case ProductStockFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        ProductStocksListUrlFiltersWithMultipleValues.brand
      );

    case ProductStockFilterKeys.updateDate:
      return getMinMaxQueryParam(
        filter,
        ProductStocksListUrlFiltersEnum.updateFrom,
        ProductStocksListUrlFiltersEnum.updateTo
      );

    case ProductStockFilterKeys.stockUpdateDate:
      return getMinMaxQueryParam(
        filter,
        ProductStocksListUrlFiltersEnum.stockUpdateFrom,
        ProductStocksListUrlFiltersEnum.stockUpdateTo
      );
    case ProductStockFilterKeys.productStatus:
      return getMultipleValueQueryParam(
        filter,
        ProductStocksListUrlFiltersWithMultipleValues.productStatus
      );
    case ProductStockFilterKeys.trackInventory:
      return getSingleEnumValueQueryParam(
        filter,
        ProductStocksListUrlFiltersEnum.trackInventory,
        TrackInventoryBoolean
      );
    case ProductStockFilterKeys.vertical:
      return getMultipleValueQueryParam(
        filter,
        ProductStocksListUrlFiltersWithMultipleValues.vertical
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<ProductStocksListUrlFilters>(
  ProductStocks_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  ProductStocksListUrlQueryParams,
  ProductStocksListUrlFilters
>({
  ...ProductStocksListUrlFiltersEnum,
  ...ProductStocksListUrlFiltersWithMultipleValues
});
